/**
 * Geocoding Types
 *
 * TypeScript interfaces for geocoding functionality including
 * Nominatim API responses and MapLibre geocoder integration.
 */

/**
 * Geographic coordinates
 */
export interface GeoCoordinates {
  /** Longitude (East-West position) */
  lng: number;
  /** Latitude (North-South position) */
  lat: number;
}

/**
 * Geographic bounding box
 */
export interface GeoBoundingBox {
  /** Southwest corner */
  southwest: GeoCoordinates;
  /** Northeast corner */
  northeast: GeoCoordinates;
}

/**
 * Address components from geocoding result
 */
export interface AddressComponents {
  /** House number */
  house_number?: string;
  /** Street name */
  road?: string;
  /** Neighbourhood/district */
  neighbourhood?: string;
  /** Suburb */
  suburb?: string;
  /** City/town */
  city?: string;
  /** Town (smaller than city) */
  town?: string;
  /** Village */
  village?: string;
  /** Municipality/gmina */
  municipality?: string;
  /** County/powiat */
  county?: string;
  /** State/voivodeship */
  state?: string;
  /** Postal code */
  postcode?: string;
  /** Country */
  country?: string;
  /** Country code (ISO 3166-1 alpha-2) */
  country_code?: string;
}

/**
 * Nominatim API raw response for a single result
 */
export interface NominatimResult {
  /** Unique identifier for the place */
  place_id: number;
  /** OSM type (node, way, relation) */
  osm_type: 'node' | 'way' | 'relation';
  /** OSM identifier */
  osm_id: number;
  /** Formatted display name */
  display_name: string;
  /** Place type */
  type: string;
  /** Place class */
  class: string;
  /** Importance score (0-1) */
  importance: number;
  /** Icon URL for the place type */
  icon?: string;
  /** Latitude */
  lat: string;
  /** Longitude */
  lon: string;
  /** Bounding box [south, north, west, east] */
  boundingbox: [string, string, string, string];
  /** Address components */
  address?: AddressComponents;
}

/**
 * Nominatim GeoJSON Feature properties
 */
export interface NominatimGeoJSONProperties {
  /** Unique identifier for the place */
  place_id: number;
  /** OSM type (node, way, relation) */
  osm_type: 'node' | 'way' | 'relation';
  /** OSM identifier */
  osm_id: number;
  /** Formatted display name */
  display_name: string;
  /** Place type */
  type: string;
  /** Place class */
  class: string;
  /** Importance score (0-1) */
  importance: number;
  /** Icon URL for the place type */
  icon?: string;
  /** Address components */
  address?: AddressComponents;
}

/**
 * GeoJSON Point feature from Nominatim
 */
export interface NominatimGeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: NominatimGeoJSONProperties;
  bbox?: [number, number, number, number]; // [west, south, east, north]
}

/**
 * Nominatim GeoJSON response
 */
export interface NominatimGeoJSONResponse {
  type: 'FeatureCollection';
  licence: string;
  features: NominatimGeoJSONFeature[];
}

/**
 * MapLibre Geocoder API compatible feature
 */
export interface MapLibreGeocoderFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  /** Human-readable place name */
  place_name: string;
  /** Raw properties from Nominatim */
  properties: NominatimGeoJSONProperties;
  /** Short text representation */
  text: string;
  /** Place type for categorization */
  place_type: string[];
  /** Center coordinates [lng, lat] */
  center: [number, number];
  /** Bounding box [west, south, east, north] */
  bbox?: [number, number, number, number];
  /** Address context hierarchy */
  context?: Array<{
    id: string;
    text: string;
    short_code?: string;
  }>;
}

/**
 * MapLibre Geocoder API response
 */
export interface MapLibreGeocoderResponse {
  features: MapLibreGeocoderFeature[];
}

/**
 * Geocoding request parameters
 */
export interface GeocodingRequest {
  /** Search query string */
  query: string;
  /** Geographic bounds to prioritize results */
  proximity?: GeoCoordinates;
  /** Bounding box to limit search area */
  bbox?: GeoBoundingBox;
  /** Maximum number of results */
  limit?: number;
  /** Language preference */
  language?: string;
  /** Country codes to restrict search */
  countries?: string[];
}

/**
 * Reverse geocoding request parameters
 */
export interface ReverseGeocodingRequest {
  /** Coordinates to reverse geocode */
  coordinates: GeoCoordinates;
  /** Language preference */
  language?: string;
  /** Administrative level to focus on */
  zoom?: number;
  /**
   * When true, perform a worldwide reverse-geocode that ignores the
   * Poland-specific defaults (countrycodes / viewbox / bounded). Used by the
   * per-workspace map configurator to resolve centers outside Poland.
   */
  unbounded?: boolean;
}

/**
 * Geocoding error types
 */
export enum GeocodingErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  INVALID_QUERY = 'INVALID_QUERY',
  NO_RESULTS = 'NO_RESULTS',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Geocoding error information
 */
export interface GeocodingError {
  type: GeocodingErrorType;
  message: string;
  originalError?: Error;
  statusCode?: number;
  retryAfter?: number; // seconds to wait before retry
}

/**
 * Geocoding result with success/error state
 */
export type GeocodingResult =
  | { success: true; data: MapLibreGeocoderResponse }
  | { success: false; error: GeocodingError };

/**
 * Place type categories for search filtering
 */
export enum PlaceTypeCategory {
  ADDRESS = 'address',
  ADMINISTRATIVE = 'administrative',
  POINT_OF_INTEREST = 'poi',
  NATURAL = 'natural',
  TRANSPORTATION = 'transportation',
}

/**
 * Polish-specific place types
 */
export interface PolishPlaceTypes {
  /** Voivodeship (województwo) */
  voivodeship: string;
  /** Powiat (county) */
  powiat: string;
  /** Gmina (municipality) */
  gmina: string;
  /** Miasto (city) */
  miasto: string;
  /** Wieś (village) */
  wies: string;
  /** Dzielnica (district) */
  dzielnica: string;
}